// Quotation pricing for the admin document generator.
//
// ONE implementation, used by three callers that must agree to the unit: the
// live preview, the admin profit panel, and the export worker that renders the
// PDF (docgen-worker copies this folder at build time). Pure functions only —
// no React, no Supabase, no DOM — so the worker can import it unchanged.
//
// The business rules (owner, 2026-09-28; docs/docgen-handoff/BRIEF-*.md):
//   cost        = supplier price × (1 + agent %)            per line, in its own currency
//   converted   → quote currency with the document's internal rates
//                 (USD→SAR fx_usd_sar, default 3.78; CNY→USD fx_cny_usd, no default)
//   client      = cost × (1 + margin %) for goods; cost as-is for pass-through
//                 (shipping, customs, clearance, delivery carry no margin)
//   unit price  = client rounded half-up to a whole unit; total = unit × qty,
//                 so every total on the page reconciles exactly
//   itemized    = each section's per-unit subtotal is rounded, and the rounding
//                 remainder goes to the largest section so the subtotals sum
//                 exactly to the unit price
//   deposit     = total × deposit %, minus the amount already paid
//
// ⚠️ TWO OUTPUTS, KEPT APART ON PURPOSE. `clientView` is the ONLY thing a
// renderer may receive. It is built field by field from a whitelist, so a cost,
// the agent %, the margin or an FX rate cannot reach a client document by
// being carried along on a spread. `internalView` is for the admin profit
// panel and the approval snapshot, never for a page. pricing.test.js pins both.
//
// The margin exists only inside these documents. The platform's "no mark-up,
// ever" rule (CLAUDE.md, 2026-09-19) governs offers and is untouched: nothing
// here reads or writes offers or any platform price.

export const CURRENCIES = ['SAR', 'USD'];
export const COST_CURRENCIES = ['USD', 'CNY', 'SAR'];

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// Half-up at `dp` decimals, immune to binary noise. A float chain whose true
// value is x.5 can land on x.4999999999 and would round DOWN; settling at 6
// decimals first (below any figure we print) restores the .5, so 1.005 → 1.01
// and 0.1 × 3 × 5 = 1.5000000000000002 behaves like 1.5.
export function roundHalfUp(value, dp = 0) {
  const settled = Number(Math.round(Number(`${value}e6`)) + 'e-6');
  const sign = settled < 0 ? -1 : 1;
  const shifted = Number(`${Math.abs(settled)}e${dp}`);
  return sign * Number(`${Math.round(shifted)}e-${dp}`);
}

// Value of one unit of `currency` in SAR, from the document's internal rates.
// Returns null when the rate that currency needs is missing.
function sarPerUnit(currency, doc) {
  const usdSar = num(doc.fx_usd_sar);
  const cnyUsd = num(doc.fx_cny_usd);
  if (currency === 'SAR') return 1;
  if (currency === 'USD') return usdSar > 0 ? usdSar : null;
  if (currency === 'CNY') return usdSar > 0 && cnyUsd > 0 ? cnyUsd * usdSar : null;
  return null;
}

export function convert(amount, from, to, doc) {
  const a = sarPerUnit(from, doc);
  const b = sarPerUnit(to, doc);
  if (a === null || b === null) return null;
  return (amount * a) / b;
}

// Per-unit cost of one line or lump, in the quote currency, split into the
// supplier part and the agent part. `errors` collects why it cannot be priced.
function costOf(amount, currency, doc, errors, where) {
  const price = num(amount);
  if (price === null || !Number.isFinite(price)) {
    errors.push({ code: 'missing_cost', ...where });
    return null;
  }
  if (!COST_CURRENCIES.includes(currency)) {
    errors.push({ code: 'missing_cost_currency', ...where });
    return null;
  }
  const quote = doc.currency;
  const supplier = convert(price, currency, quote, doc);
  if (supplier === null) {
    errors.push({ code: currency === 'CNY' ? 'missing_fx_cny_usd' : 'missing_fx_usd_sar', ...where });
    return null;
  }
  const agentPct = num(doc.agent_pct) || 0;
  return { supplier, agent: supplier * (agentPct / 100) };
}

const byPosition = (a, b) => (num(a.position) || 0) - (num(b.position) || 0);

// The client-facing text of a line. Whitelisted: adding a field here is the
// only way it can reach a PDF.
function lineText(line) {
  return {
    id: line.id,
    item: line.item || '',
    description: line.description || '',
    brand: line.brand || '',
    unit: line.unit || '',
    qty: num(line.qty),
  };
}

/**
 * Price a quotation.
 *
 * @param {object} doc       doc_documents row (currency, agent_pct, margin_pct,
 *                           fx_usd_sar, fx_cny_usd, deposit_pct, paid_amount, layout)
 * @param {object[]} sections doc_sections rows
 * @param {object[]} lines    doc_lines rows
 * @returns {{ clientView: object, internalView: object, errors: object[] }}
 *          errors non-empty ⇒ the document must not be exported.
 */
export function computeQuotation(doc, sections, lines) {
  const errors = [];
  const currency = doc.currency;
  if (!CURRENCIES.includes(currency)) errors.push({ code: 'bad_currency' });

  const marginPct = num(doc.margin_pct) || 0;
  const markup = 1 + marginPct / 100;
  const itemized = doc.layout === 'itemized';

  const secs = [...(sections || [])].sort(byPosition);
  const linesBySection = new Map(secs.map((s) => [s.id, []]));
  for (const l of lines || []) {
    if (linesBySection.has(l.section_id)) linesBySection.get(l.section_id).push(l);
    else errors.push({ code: 'orphan_line', lineId: l.id });
  }
  for (const arr of linesBySection.values()) arr.sort(byPosition);

  // Internal accumulators, all in the quote currency.
  const acc = { supplier: 0, agent: 0, goodsCost: 0, passthroughCost: 0 };
  const addCost = (c, qty, kind) => {
    acc.supplier += c.supplier * qty;
    acc.agent += c.agent * qty;
    if (kind === 'passthrough') acc.passthroughCost += (c.supplier + c.agent) * qty;
    else acc.goodsCost += (c.supplier + c.agent) * qty;
  };

  const clientSections = [];
  let total = 0;
  let summary = null;

  if (!itemized) {
    // ── Simple: every line carries its own rounded unit price. ──────────
    for (const s of secs) {
      const outLines = [];
      for (const l of linesBySection.get(s.id)) {
        const where = { sectionId: s.id, lineId: l.id };
        const qty = num(l.qty);
        if (qty === null || !(qty >= 0)) errors.push({ code: 'missing_qty', ...where });
        const c = costOf(l.supplier_unit_cost, l.cost_currency, doc, errors, where);
        let unitPrice = null;
        let lineTotal = null;
        if (c && qty !== null) {
          const kind = l.line_kind === 'passthrough' ? 'passthrough' : 'goods';
          const unitCost = c.supplier + c.agent;
          unitPrice = roundHalfUp(kind === 'goods' ? unitCost * markup : unitCost, 0);
          lineTotal = roundHalfUp(unitPrice * qty, 2);
          addCost(c, qty, kind);
          total += lineTotal;
        }
        outLines.push({ ...lineText(l), unitPrice, total: lineTotal });
      }
      clientSections.push({ id: s.id, title: s.title || '', lines: outLines });
    }
  } else {
    // ── Itemized: per-unit section subtotals that sum to the unit price. ──
    const unitCounts = new Set(secs.map((s) => num(s.units) || 1));
    if (unitCounts.size > 1) errors.push({ code: 'itemized_units_mismatch' });
    const units = secs.length ? num(secs[0].units) || 1 : 1;

    const raws = [];
    for (const s of secs) {
      const sLines = linesBySection.get(s.id);
      let raw = 0;
      let priced = true;
      if (num(s.lump_cost) !== null) {
        const c = costOf(s.lump_cost, s.lump_currency, doc, errors, { sectionId: s.id });
        if (c) {
          raw = (c.supplier + c.agent) * markup;
          addCost(c, units, 'goods');
        } else priced = false;
      } else {
        if (!sLines.length) errors.push({ code: 'empty_section', sectionId: s.id });
        for (const l of sLines) {
          const where = { sectionId: s.id, lineId: l.id };
          const qty = num(l.qty);
          if (qty === null || !(qty >= 0)) {
            errors.push({ code: 'missing_qty', ...where });
            priced = false;
            continue;
          }
          const c = costOf(l.supplier_unit_cost, l.cost_currency, doc, errors, where);
          if (!c) { priced = false; continue; }
          const kind = l.line_kind === 'passthrough' ? 'passthrough' : 'goods';
          const unitCost = c.supplier + c.agent;
          raw += (kind === 'goods' ? unitCost * markup : unitCost) * qty;
          addCost(c, qty * units, kind);
        }
      }
      raws.push(priced ? raw : null);
      clientSections.push({
        id: s.id,
        title: s.title || '',
        subtotal: null, // filled below, once every section is priced
        lines: sLines.map(lineText),
      });
    }

    if (raws.every((r) => r !== null) && raws.length) {
      const unitPrice = roundHalfUp(raws.reduce((a, b) => a + b, 0), 0);
      const rounded = raws.map((r) => roundHalfUp(r, 0));
      const remainder = unitPrice - rounded.reduce((a, b) => a + b, 0);
      if (remainder !== 0) {
        let largest = 0;
        raws.forEach((r, i) => { if (r > raws[largest]) largest = i; });
        rounded[largest] += remainder;
      }
      rounded.forEach((v, i) => { clientSections[i].subtotal = v; });
      total = roundHalfUp(unitPrice * units, 2);
      summary = { units, unitPrice, total };
    }
  }

  total = roundHalfUp(total, 2);
  const depositPct = num(doc.deposit_pct) ?? 50;
  const paid = num(doc.paid_amount) || 0;
  const depositAmount = roundHalfUp(total * (depositPct / 100), 2);
  const remaining = roundHalfUp(depositAmount - paid, 2);
  if (remaining < 0) errors.push({ code: 'paid_exceeds_deposit' });

  const clientView = {
    currency,
    layout: itemized ? 'itemized' : 'simple',
    sections: clientSections,
    summary,
    total,
    deposit: { pct: depositPct, amount: depositAmount, paid, remaining },
  };

  // ── Internal: the admin's profit check. Never passed to a renderer. ────
  const cost = acc.goodsCost + acc.passthroughCost;
  const margin = total - cost;
  const inBoth = (v) => ({
    [currency]: roundHalfUp(v, 2),
    ...(currency === 'SAR'
      ? { USD: roundHalfUp(convert(v, 'SAR', 'USD', doc) ?? NaN, 2) }
      : { SAR: roundHalfUp(convert(v, 'USD', 'SAR', doc) ?? NaN, 2) }),
  });
  const internalView = {
    currency,
    supplierCost: inBoth(acc.supplier),
    agentCommission: inBoth(acc.agent),
    goodsCost: inBoth(acc.goodsCost),
    passthroughCost: inBoth(acc.passthroughCost),
    cost: inBoth(cost),
    clientTotal: inBoth(total),
    margin: inBoth(margin),
    // Primary: margin ÷ cost (owner, 2026-09-28). Lower than margin_pct when
    // pass-through lines are present; rounding moves it slightly either way.
    marginOnCostPct: cost > 0 ? roundHalfUp((margin / cost) * 100, 2) : null,
    marginOnTotalPct: total > 0 ? roundHalfUp((margin / total) * 100, 2) : null,
    settings: {
      agentPct: num(doc.agent_pct) || 0,
      marginPct,
      fxUsdSar: num(doc.fx_usd_sar),
      fxCnyUsd: num(doc.fx_cny_usd),
    },
  };

  return { clientView, internalView, errors };
}
